/* eslint-disable @atlaskit/editor/no-re-export */
// Entry file in package.json

import memoizeOne from 'memoize-one';

import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import {
	nativeEmbedsFallbackTransform,
	transformContainerNodes,
	transformMediaLinkMarks,
	transformNestedTablesIncomingDocument,
} from '@atlaskit/adf-utils/transforms';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { UnsupportedContentLevelsTracking } from '@atlaskit/editor-common/utils';
import {
	findAndTrackUnsupportedContentNodes,
	validateADFEntity,
} from '@atlaskit/editor-common/utils';
import type { ADFStage } from '@atlaskit/editor-common/validator';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { PLATFORM } from './analytics/events';
import type { AnalyticsEventPayload } from './analytics/events';
import { trackUnsupportedContentLevels } from './analytics/unsupported-content';
import type { Serializer } from './serializer';
import { countNodes } from './ui/Renderer/count-nodes';
import type { RendererAppearance } from './ui/Renderer/types';

export interface RenderOutput<T> {
	pmDoc?: PMNode;
	result: T;
	stat: RenderOutputStat;
}

export interface RenderOutputStat {
	buildTreeTime?: number;
	nodesCount?: Record<string, number>;
	sanitizeTime: number;
	serializeTime?: number;
}

export interface ResultWithTime<T> {
	output: T;
	time: number;
}

const SUPPORTS_HIRES_TIMER_API = !!(
	typeof window !== 'undefined' &&
	window.performance &&
	performance.now
);

const withStopwatch = <T>(cb: () => T): ResultWithTime<T> => {
	const startTime = SUPPORTS_HIRES_TIMER_API ? performance.now() : Date.now();
	const output = cb();
	const endTime = SUPPORTS_HIRES_TIMER_API ? performance.now() : Date.now();
	const time = endTime - startTime;

	return { output, time };
};

type DispatchAnalyticsEvent = (event: AnalyticsEventPayload) => void;

/** Schema-variant escape hatches handed to the ADF validator. */
type ValidationOverrides = { allowNestedTables?: boolean; allowTableInPanel?: boolean };

const _validation = (
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	doc: any,
	schema: Schema,
	adfStage: ADFStage | undefined,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
	skipValidation?: boolean,
	validationOverrides?: ValidationOverrides,
) => {
	// link mark on mediaSingle is deprecated, need to move link mark to child media node
	// https://product-fabric.atlassian.net/browse/ED-14043
	const { transformedAdf, isTransformed } = transformMediaLinkMarks(doc);
	if (isTransformed && dispatchAnalyticsEvent) {
		dispatchAnalyticsEvent({
			action: ACTION.MEDIA_LINK_TRANSFORMED,
			actionSubject: ACTION_SUBJECT.RENDERER,
			eventType: EVENT_TYPE.OPERATIONAL,
		});
	}

	// Forward `adfStage` only when a caller names one. Omitting it keeps stage-0 specs acceptable,
	// which is what a renderer wants: stored documents can contain them, and wrapping such content
	// as unsupported is worse than rendering it. A caller that declares `final` asks for full-ADF
	// strictness and gets it.
	let result = skipValidation
		? transformedAdf || doc
		: validateADFEntity(
				schema,
				transformedAdf || doc,
				dispatchAnalyticsEvent,
				validationOverrides,
				adfStage,
			);

	// The spec validator can return the original root object when malformed JSON has no node type.
	// Reject it here, matching the legacy validator, before ProseMirror attempts to deserialize it.
	if (!result || typeof result.type !== 'string' || result.type.length === 0) {
		return null;
	}

	// ProseMirror always require a child under doc
	if (result.type === 'doc') {
		if (Array.isArray(result.content) && result.content.length === 0) {
			result.content.push({
				type: 'paragraph',
				content: [],
			});
		}

		// Just making sure doc is always valid
		if (!result.version) {
			result.version = 1;
		}
	}

	// Convert nested-table extensions into nested tables
	try {
		const { transformedAdf, isTransformed } = transformNestedTablesIncomingDocument(result);

		if (isTransformed) {
			dispatchAnalyticsEvent?.({
				action: ACTION.NESTED_TABLE_TRANSFORMED,
				actionSubject: ACTION_SUBJECT.RENDERER,
				eventType: EVENT_TYPE.OPERATIONAL,
			});

			result = transformedAdf;
		}
	} catch (e) {
		dispatchAnalyticsEvent?.({
			action: ACTION.INVALID_PROSEMIRROR_DOCUMENT,
			actionSubject: ACTION_SUBJECT.RENDERER,
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: {
				platform: PLATFORM.WEB,
				errorStack: `${e instanceof Error && e.name === 'NodeNestingTransformError' ? 'NodeNestingTransformError - Failed to encode one or more nested tables' : undefined}`,
			},
		});
	}

	// Upgrade panel → panel_c1 where the schema allows it
	if (result && expValEquals('platform_editor_nest_table_in_panel', 'isEnabled', true)) {
		try {
			const { transformedAdf, isTransformed, transformedNodeTypes } = transformContainerNodes(
				result,
				schema,
			);
			if (isTransformed && transformedAdf) {
				dispatchAnalyticsEvent?.({
					action: ACTION.CONTAINER_NODE_TRANSFORMED,
					actionSubject: ACTION_SUBJECT.RENDERER,
					eventType: EVENT_TYPE.OPERATIONAL,
					attributes: {
						transformedNodeTypes,
					},
				});

				result = transformedAdf;
			}
		} catch (e) {
			dispatchAnalyticsEvent?.({
				action: ACTION.INVALID_PROSEMIRROR_DOCUMENT,
				actionSubject: ACTION_SUBJECT.RENDERER,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					platform: PLATFORM.WEB,
					errorStack: `${e instanceof Error && e.name === 'NodeNestingTransformError' ? 'NodeNestingTransformError - Failed to transform panel to panel_c1' : undefined}`,
				},
			});
		}
	}

	if (
		result &&
		!expValEquals('cc-maui-experiment', 'isEnabled', true) &&
		!fg('platform_native_embeds_rollout_non_maui_experience') &&
		fg('platform_editor_native_embeds_fallback_transform')
	) {
		const { transformedAdf, hasValidTransform } = nativeEmbedsFallbackTransform(result, schema);

		if (hasValidTransform && transformedAdf) {
			dispatchAnalyticsEvent?.({
				action: ACTION.NATIVE_EMBEDS_TRANSFORMED,
				actionSubject: ACTION_SUBJECT.RENDERER,
				eventType: EVENT_TYPE.OPERATIONAL,
			});

			result = transformedAdf;
		}
	}

	return result;
};

const memoValidation = memoizeOne(_validation, (newArgs, lastArgs) => {
	type ValidationArgsType = [
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		doc: any,
		schema: Schema,
		stage: ADFStage | undefined,
		DispatchAnalyticsEvent?: DispatchAnalyticsEvent | undefined,
		skipValidation?: boolean | undefined,
		validationOverrides?: ValidationOverrides,
	];

	const [
		newDoc,
		newSchema,
		newADFStage,
		,
		/* ignoring dispatchAnalyticsEvent */ newSkipValidation,
		newValidationOverrides,
	]: ValidationArgsType = newArgs;
	const [
		oldDoc,
		oldSchema,
		oldADFStage,
		,
		/* ignoring dispatchAnalyticsEvent */ oldSkipValidation,
		oldValidationOverrides,
	]: ValidationArgsType = lastArgs;

	// `areDocsEqual` may stringify the whole document, so it goes last and only runs once everything
	// cheaper has matched.
	return (
		newSchema === oldSchema &&
		newADFStage === oldADFStage &&
		newSkipValidation === oldSkipValidation &&
		areValidationOverridesEqual(newValidationOverrides, oldValidationOverrides) &&
		areDocsEqual(newDoc, oldDoc)
	);
});

// Ignored via go/ees005
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const areDocsEqual = (docA: any, docB: any) => {
	if (docA === docB) {
		return true;
	}

	if (typeof docA === 'string' && typeof docB === 'string') {
		return docA === docB;
	}

	// PMNode
	if (docA.type && docA.toJSON && docB.type && docB.toJSON) {
		// `Node.eq` compares markup and content directly; stringifying both `toJSON` trees gives the
		// same answer ~14x slower on a 5k-node document.
		if (typeof docA.eq === 'function' && typeof docB.eq === 'function') {
			return docA.eq(docB);
		}
		return JSON.stringify(docA.toJSON()) === JSON.stringify(docB.toJSON());
	}

	// Object
	return JSON.stringify(docA) === JSON.stringify(docB);
};

/**
 * `Renderer` rebuilds this object in its render body, so a reference check never matches and the memo
 * re-validates (and re-transforms) an unchanged document every render. A flat bag of optional
 * booleans, so a shallow key/value comparison is exact and free next to what it protects.
 */
const areValidationOverridesEqual = (a?: ValidationOverrides, b?: ValidationOverrides): boolean => {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}
	const aEntries = Object.entries(a);
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const bRecord = b as Record<string, any>;
	return (
		aEntries.length === Object.keys(b).length &&
		aEntries.every(([key, value]) => bRecord[key] === value)
	);
};

const _serializeFragment = <T>(serializer: Serializer<T>, doc: PMNode): T | null => {
	return serializer.serializeFragment(doc.content);
};
const memoSerializeFragment = memoizeOne(_serializeFragment, (newArgs, lastArgs) => {
	const [newSerializer, newDoc] = newArgs;
	const [oldSerializer, oldDoc] = lastArgs;

	return newSerializer === oldSerializer && areDocsEqual(newDoc, oldDoc);
});

const _createNodeAndCheck = (
	schema: Schema,
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	doc: any,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
): PMNode => {
	const pmNode = schema.nodeFromJSON(doc);
	try {
		pmNode.check();
	} catch (err) {
		if (dispatchAnalyticsEvent) {
			dispatchAnalyticsEvent({
				action: ACTION.INVALID_PROSEMIRROR_DOCUMENT,
				actionSubject: ACTION_SUBJECT.RENDERER,
				attributes: {
					platform: PLATFORM.WEB,
					errorStack: err instanceof Error ? err.message : String(err),
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			});
		}
	}
	return pmNode;
};
const memoCreateNodeAndCheck = memoizeOne(_createNodeAndCheck, (newArgs, lastArgs) => {
	// ignore dispatchAnalyticsEvent
	const [newSchema, newDoc] = newArgs;
	const [oldSchema, oldDoc] = lastArgs;

	return newSchema === oldSchema && areDocsEqual(newDoc, oldDoc);
});

export const renderDocument = <T>(
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	doc: any,
	serializer: Serializer<T>,
	schema: Schema = defaultSchema,
	adfStage?: ADFStage,
	rendererId: string = 'noid',
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
	unsupportedContentLevelsTracking?: UnsupportedContentLevelsTracking,
	appearance?: RendererAppearance,
	includeNodesCountInStats?: boolean,
	skipValidation?: boolean,
	validationOverrides?: ValidationOverrides,
): RenderOutput<T | null> => {
	const stat: RenderOutputStat = { sanitizeTime: 0 };

	const { output: validDoc, time: sanitizeTime } = withStopwatch(() => {
		return memoValidation(
			doc,
			schema,
			adfStage,
			dispatchAnalyticsEvent,
			skipValidation,
			validationOverrides,
		);
	});

	// save sanitize time to stats
	stat.sanitizeTime = sanitizeTime;

	if (!validDoc) {
		return { stat, result: null };
	}

	const { output: node, time: buildTreeTime } = withStopwatch<PMNode>(() => {
		return memoCreateNodeAndCheck(schema, validDoc, dispatchAnalyticsEvent);
	});

	// save build tree time to stats
	stat.buildTreeTime = buildTreeTime;

	const { output: result, time: serializeTime } = withStopwatch<T | null>(() => {
		return memoSerializeFragment(serializer, node) as T | null;
	});

	// save serialize tree time to stats
	stat.serializeTime = serializeTime;

	if (dispatchAnalyticsEvent) {
		findAndTrackUnsupportedContentNodes(node, schema, dispatchAnalyticsEvent);

		if (unsupportedContentLevelsTracking?.enabled) {
			const documentData = { doc: validDoc, appearance, rendererId };
			trackUnsupportedContentLevels(
				documentData,
				unsupportedContentLevelsTracking,
				dispatchAnalyticsEvent,
			);
		}
	}

	if (includeNodesCountInStats) {
		stat.nodesCount = countNodes(doc);
	}

	return { result, stat, pmDoc: node };
};
